export enum SourcingRequestStatus {
	DRAFT = 'draft',
	OPEN = 'open',
	IN_PROGRESS = 'in_progress',
	PARTIALLY_FULFILLED = 'partially_fulfilled',
	FULFILLED = 'fulfilled',
	CANCELLED = 'cancelled',
}

export type SourcingRequestStatusKey = keyof typeof SourcingRequestStatus;
export type SourcingRequestStatusValue =
	(typeof SourcingRequestStatus)[SourcingRequestStatusKey];

export const SourcingRequestStatusLabels: Record<
	SourcingRequestStatusValue,
	string
> = {
	[SourcingRequestStatus.DRAFT]: 'Draft',
	[SourcingRequestStatus.OPEN]: 'Open',
	[SourcingRequestStatus.IN_PROGRESS]: 'In Progress',
	[SourcingRequestStatus.PARTIALLY_FULFILLED]: 'Partially Fulfilled',
	[SourcingRequestStatus.FULFILLED]: 'Fulfilled',
	[SourcingRequestStatus.CANCELLED]: 'Cancelled',
};

export function getSourcingRequestStatusLabel(
	status: string | null | undefined,
): string {
	if (status == null) {
		return '-';
	}

	return (
		SourcingRequestStatusLabels[status as SourcingRequestStatusValue] ??
		'Unknown'
	);
}

export function getSourcingRequestStatusColor(
	status: string | null | undefined,
): string {
	if (status == null) {
		return 'default';
	}

	switch (status) {
		case SourcingRequestStatus.DRAFT:
			return 'secondary';
		case SourcingRequestStatus.OPEN:
			return 'info';
		case SourcingRequestStatus.IN_PROGRESS:
			return 'primary';
		case SourcingRequestStatus.PARTIALLY_FULFILLED:
			return 'warning';
		case SourcingRequestStatus.FULFILLED:
			return 'success';
		case SourcingRequestStatus.CANCELLED:
			return 'danger';
		default:
			return 'default';
	}
}

/**
 * Static value→{label, color} mapping for the list-builder
 * `styleConfig.valueMapping` on a `displayStyle: 'badge'` field. Keeps
 * the SR table column + any future status badge pinned to the same
 * palette: gray Draft → blue Open → amber In Progress → amber Partial
 * → green Fulfilled → red Cancelled.
 */
export const SourcingRequestStatusBadgeValueMapping: ReadonlyArray<{
	value: SourcingRequestStatusValue;
	displayText: string;
	color: { text: string; bg: string };
}> = [
	{
		value: SourcingRequestStatus.DRAFT,
		displayText: SourcingRequestStatusLabels[SourcingRequestStatus.DRAFT],
		color: { text: '#475569', bg: '#e2e8f0' },
	},
	{
		value: SourcingRequestStatus.OPEN,
		displayText: SourcingRequestStatusLabels[SourcingRequestStatus.OPEN],
		color: { text: '#1d4ed8', bg: '#dbeafe' },
	},
	{
		value: SourcingRequestStatus.IN_PROGRESS,
		displayText: SourcingRequestStatusLabels[SourcingRequestStatus.IN_PROGRESS],
		color: { text: '#b45309', bg: '#fef3c7' },
	},
	{
		value: SourcingRequestStatus.PARTIALLY_FULFILLED,
		displayText:
			SourcingRequestStatusLabels[SourcingRequestStatus.PARTIALLY_FULFILLED],
		color: { text: '#b45309', bg: '#fef3c7' },
	},
	{
		value: SourcingRequestStatus.FULFILLED,
		displayText: SourcingRequestStatusLabels[SourcingRequestStatus.FULFILLED],
		color: { text: '#15803d', bg: '#dcfce7' },
	},
	{
		value: SourcingRequestStatus.CANCELLED,
		displayText: SourcingRequestStatusLabels[SourcingRequestStatus.CANCELLED],
		color: { text: '#b91c1c', bg: '#fee2e2' },
	},
];
